import React, { useState, useEffect } from "react"

/**
 * RasiKattam — South Indian 4×4 horoscope grid (ராசி chart)
 *
 * Grid layout (indices):
 *   [ 0][ 1][ 2][ 3]
 *   [ 4][ ·][ ·][ 7]   · = null (merged centre label area)
 *   [ 8][ ·][ ·][11]
 *   [12][13][14][15]
 *
 * Props:
 *   rasiValues          — initial planet array (16 items, null for centre cells)
 *   setRasiValues       — callback(updatedArray)
 *   readOnly            — shows only selected planets; no buttons
 *   kattamOuterInjectClass — extra classes for the outer border box
 *   kattamInnerInjectclass — extra classes for each inner cell
 */

const DEFAULT_GRID = [
    [], [], [], [],
    [], null, null, [],
    [], null, null, [],
    [], [], [], [],
]

// Planets arranged in rows matching the screenshot layout
const PLANET_ROWS = [
    ["சூ", "சந்", "செவ்", "பு", "குரு"],
    ["சுக்", "சனி", "ரா",  "கே", "ல"],
    ["மா"],
]
const ALL_PLANETS = PLANET_ROWS.flat()

const RasiKattam = ({
    rasiValues,
    setRasiValues,
    readOnly = false,
    kattamOuterInjectClass,
    kattamInnerInjectclass,
}) => {
    const [grid, setGrid] = useState(
        rasiValues && rasiValues.length === 16 ? rasiValues : DEFAULT_GRID
    )

    // Sync grid when parent loads API data after mount
    useEffect(() => {
        if (rasiValues && rasiValues.length === 16) {
            setGrid(rasiValues)
        }
    }, [rasiValues]) // eslint-disable-line

    const toggle = (cellIdx, planet) => {
        if (grid[cellIdx] === null) return
        const next = grid.map((cell, i) => {
            if (cell === null) return null
            const has = cell.includes(planet)
            if (i === cellIdx) return has ? cell.filter(p => p !== planet) : [...cell, planet]
            // remove from every other cell (each planet lives in exactly one house)
            return has ? cell.filter(p => p !== planet) : cell
        })
        setGrid(next)
        setRasiValues?.(next)
    }

    const outerCls = kattamOuterInjectClass || "w-full"
    const cellCls  = kattamInnerInjectclass  || "min-h-[100px]"

    return (
        <div className={`relative border-2 border-black ${outerCls}`}>
            {/* 4×4 grid */}
            <div className="grid grid-cols-4 grid-rows-4 h-full">
                {grid.map((cell, i) => (
                    <div
                        key={i}
                        className={`${cellCls} ${
                            cell === null
                                ? "border-none"
                                : "border border-black flex flex-col items-center justify-center"
                        }`}
                    >
                        {cell !== null && (
                            readOnly ? (
                                /* ── Read-only: show selected planets only ── */
                                <div className="flex flex-wrap justify-center gap-x-1 gap-y-0.5 p-1">
                                    {cell.map(p => (
                                        <span key={p} className="text-sm font-bold text-black leading-tight">{p}</span>
                                    ))}
                                </div>
                            ) : (
                                /* ── Edit: show all planets as toggle buttons ── */
                                <div className="flex flex-col items-center gap-1 p-1">
                                    {PLANET_ROWS.map((row, ri) => (
                                        <div key={ri} className="flex gap-1 flex-wrap justify-center">
                                            {row.map(p => (
                                                <button
                                                    key={p}
                                                    type="button"
                                                    onClick={() => toggle(i, p)}
                                                    className={`text-sm leading-tight px-1.5 py-0.5 border rounded transition-colors
                                                        ${cell.includes(p)
                                                            ? "bg-primeColor text-white border-primeColor font-bold"
                                                            : "bg-white text-black border-gray-300 hover:border-primeColor"
                                                        }`}
                                                >
                                                    {p}
                                                </button>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            )
                        )}
                    </div>
                ))}
            </div>

            {/* Centre label — absolutely positioned over the null cells */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="text-2xl font-bold text-black select-none">ராசி</span>
            </div>
        </div>
    )
}

export default RasiKattam
